// src/merch/PaintTools.jsx
//
// Drawing toolbar, shared by the side panel and the full-screen editor.

import React from "react";
import { useSnapshot } from "valtio";
import { merch } from "./store";
import { undoStroke, clearPaint, commitPaintAsLayer } from "./paint";

export const PAINT_COLOURS = ["#111111", "#ffffff", "#a2332b", "#1f3a93", "#0d7845", "#c9a227"];

const BRUSHES = [
  { id: "pen", label: "Pen", hint: "Crisp lines — great for signatures" },
  { id: "marker", label: "Marker", hint: "See-through, like a highlighter" },
  { id: "soft", label: "Soft", hint: "Fuzzy edges" },
  { id: "spray", label: "Spray", hint: "Airbrush — go over it to build up colour" },
];

const setTool = (tool) => { merch.paint.tool = tool; merch.paint.erase = false; };

export default function PaintTools({ compact = false, onCommitted }) {
  const snap = useSnapshot(merch);
  const p = snap.paint;

  return (
    <div className={`ma-paint ${compact ? "compact" : ""}`}>
      <div className="ma-paint-row">
        <div className="ma-seg small" role="tablist" aria-label="Tool">
          <button aria-pressed={p.tool !== "fill" && !p.erase} onClick={() => setTool("brush")} title="Draw">✏️ Draw</button>
          <button aria-pressed={p.tool === "fill" && !p.erase} onClick={() => setTool("fill")} title="Paint bucket — tap a shape to fill it, or the background to colour the whole sticker">🪣 Fill</button>
          <button aria-pressed={p.erase} onClick={() => { merch.paint.erase = true; merch.paint.tool = "brush"; }} title="Eraser">Eraser</button>
        </div>
      </div>
      {p.tool !== "fill" && !p.erase && (
        <div className="ma-paint-row ma-brushes">
          {BRUSHES.map((b) => (
            <button
              key={b.id}
              className="ma-brush"
              aria-pressed={(p.brush || "pen") === b.id}
              title={b.hint}
              onClick={() => { merch.paint.brush = b.id; }}
            >
              <i className={`ma-brush-ico ${b.id}`} />
              {b.label}
            </button>
          ))}
        </div>
      )}
      {p.tool === "fill" && !p.erase && (
        <p className="ma-muted ma-fill-hint">Tap inside a shape to fill it — or tap the background to colour the whole sticker.</p>
      )}
      <div className="ma-paint-row">
        {PAINT_COLOURS.map((c) => (
          <button
            key={c}
            className="ma-dot"
            aria-label={`Colour ${c}`}
            aria-pressed={!p.erase && p.color === c}
            style={{ background: c }}
            onClick={() => { merch.paint.color = c; merch.paint.erase = false; }}
          />
        ))}
        <input
          type="color"
          aria-label="Custom colour"
          value={p.color}
          onChange={(e) => { merch.paint.color = e.target.value; merch.paint.erase = false; }}
        />
      </div>
      {p.tool !== "fill" && <label className="ma-paint-size">
        Size
        <input
          type="range" min={0.15} max={12} step={0.05}
          value={p.sizeMm}
          onChange={(e) => { merch.paint.sizeMm = Number(e.target.value); }}
        />
        <span>{p.sizeMm < 1 ? p.sizeMm.toFixed(2) : p.sizeMm.toFixed(1)} mm</span>
      </label>}
      {p.tool !== "fill" && !p.erase && ["soft", "marker", "spray"].includes(p.brush) && (
        <label className="ma-paint-size" title="How strong the colour is. With a pressure pen, spray also follows how hard you press.">
          Opacity
          <input
            type="range" min={10} max={100} step={5}
            value={p.opacity ?? 100}
            onChange={(e) => { merch.paint.opacity = Number(e.target.value); }}
          />
          <span>{p.opacity ?? 100}%</span>
        </label>
      )}
      {p.tool !== "fill" && (
        <label className="ma-paint-size" title="Steadies shaky lines: higher = smoother, but the line trails further behind your pen">
          <span className="ma-smooth-toggle">
            <input type="checkbox" checked={p.smooth} onChange={(e) => { merch.paint.smooth = e.target.checked; }} />
            Smooth
          </span>
          <input
            type="range" min={1} max={10} step={1}
            disabled={!p.smooth}
            value={p.smoothLevel ?? 5}
            onChange={(e) => { merch.paint.smoothLevel = Number(e.target.value); }}
          />
          <span>{p.smooth ? p.smoothLevel ?? 5 : "off"}</span>
        </label>
      )}
      <div className="ma-paint-row">
        <button className="ma-btn ghost" disabled={!p.strokes} onClick={undoStroke}>Undo</button>
        <button className="ma-btn ghost" disabled={!p.strokes} onClick={clearPaint}>Clear</button>
        {compact && (
          <button
            className="ma-btn primary"
            disabled={!p.strokes}
            onClick={() => { commitPaintAsLayer(); merch.paint.active = false; onCommitted?.(); }}
          >
            Add as layer
          </button>
        )}
      </div>
      {!compact && (
        <>
          <button
            className="ma-btn primary"
            disabled={!p.strokes}
            onClick={() => { commitPaintAsLayer(); merch.paint.active = false; onCommitted?.(); }}
          >
            Add as layer
          </button>
          <p className="ma-muted">Draw on the sticker — sign your name!</p>
        </>
      )}
    </div>
  );
}
